import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Warning,
  Info,
  DataUsage,
  Cookie,
  Share,
  Delete,
  Lock,
  Visibility,
  Shield
} from '@mui/icons-material';
import { privacyPolicyService } from '../services/firebase';
import { PrivacyPolicyData } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';
import ReactMarkdown from 'react-markdown';

// Icon mapping for dynamic icon rendering
const getIcon = (iconName: string) => {
  const iconProps = { sx: { fontSize: 24, color: 'white' } };
  
  switch (iconName) {
    case 'DataUsage': return <DataUsage {...iconProps} />;
    case 'Visibility': return <Visibility {...iconProps} />;
    case 'Lock': return <Lock {...iconProps} />;
    case 'Share': return <Share {...iconProps} />;
    case 'Delete': return <Delete {...iconProps} />;
    case 'Cookie': return <Cookie {...iconProps} />;
    case 'Security': return <Security {...iconProps} />;
    case 'Shield': return <Shield {...iconProps} />;
    default: return <Info {...iconProps} />;
  }
};

// Alert type icon mapping
const alertIconMap: { [key: string]: React.ReactElement } = {
  info: <Info />,
  warning: <Warning />,
  success: <CheckCircle />,
  error: <Warning />
};

const PrivacyPolicyPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [policyData, setPolicyData] = useState<PrivacyPolicyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        setLoading(true);
        let data = await privacyPolicyService.getPrivacyPolicyData();
        
        if (!data) {
          await privacyPolicyService.initializePrivacyPolicyData();
          data = await privacyPolicyService.getPrivacyPolicyData();
        }
        
        setPolicyData(data);
      } catch (err: any) {
        setError(t('privacy_policy_page.errors.load', { message: err.message }));
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const formattedDate = policyData?.lastUpdated 
    ? new Date(policyData.lastUpdated.toDate()).toLocaleDateString(i18n.language, { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    : '';

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(policyData?.title, i18n.language) || t('privacy_policy_page.title')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(policyData?.description, i18n.language) || t('privacy_policy_page.meta_description')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          backgroundColor: 'var(--light-gray-white)',
          py: 8,
          mb: 6
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'var(--light-blue-cta)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,91,189,0.2)'
                }}>
                  <Security sx={{ fontSize: 50, color: 'white' }} />
                </Box>
              </Box>
              
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '3rem', md: '4rem' },
                  color: 'var(--navy-blue)',
                  mb: 3
                }}
              >
                {getTranslatedData(policyData?.title, i18n.language) || t('privacy_policy_page.title')}
              </Typography>
              
              <Typography 
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  mb: 4
                }}
              >
                {getTranslatedData(policyData?.description, i18n.language) || t('privacy_policy_page.default_description')}
              </Typography>

              {/* Version Info */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`${t('privacy_policy_page.last_updated')}: ${formattedDate}`}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 3,
                    fontWeight: 400,
                    border: '1px solid var(--gray-shadow)'
                  }}
                />
                {policyData?.kvkkCompliant && (
                  <Chip
                    label={t('privacy_policy_page.kvkk_compliant')}
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 3,
                      fontWeight: 400,
                      border: '1px solid var(--gray-shadow)'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Important Notes */}
          <Box sx={{ mb: 6 }}>
            {policyData?.importantNotes
              ?.filter(note => note.isActive)
              ?.sort((a, b) => a.order - b.order)
              ?.map((note) => (
                <Alert
                  key={note.id}
                  severity={note.type}
                  icon={alertIconMap[note.type]}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {getTranslatedData(note.title, i18n.language)}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ReactMarkdown>{getTranslatedData(note.content, i18n.language) || ''}</ReactMarkdown>
                  </Typography>
                </Alert>
              ))}
          </Box>

          {/* Policy Sections */}
          <Box sx={{ display: 'grid', gap: 4, mb: 8 }}>
            {policyData?.policySections
              ?.filter(section => section.isActive)
              ?.sort((a, b) => a.order - b.order)
              ?.map((section) => (
                <Card key={section.id} sx={{ 
                  backgroundColor: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}>
                  <CardContent sx={{ p: 0 }}>
                    {/* Section Header */}
                    <Box sx={{ 
                      p: 4, 
                      backgroundColor: 'var(--light-gray-white)',
                      borderBottom: '1px solid var(--gray-shadow)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          minWidth: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: 'var(--light-blue-cta)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {getIcon(section.icon)}
                        </Box>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                          {getTranslatedData(section.title, i18n.language)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Section Content */}
                    <Box sx={{ p: 4 }}>
                       <List sx={{ p: 0 }}>
                         {section.items.map((item: any, itemIndex: number) => (
                           <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                             <ListItemIcon sx={{ minWidth: 40 }}>
                               <CheckCircle sx={{ 
                                 fontSize: 20, 
                                 color: 'var(--light-blue-cta)' 
                               }} />
                             </ListItemIcon>
                             <ListItemText 
                               primary={
                                 <Typography variant="body1" component="div">
                                   <ReactMarkdown>{getTranslatedData(item, i18n.language) || ''}</ReactMarkdown>
                                 </Typography>
                               }
                                sx={{
                                 '& .MuiListItemText-primary': {
                                   fontWeight: 400,
                                   color: 'text.primary',
                                   fontSize: '1rem'
                                 }
                               }}
                             />
                           </ListItem>
                         ))}
                       </List>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>

          {/* Contact Info */}
          <Card sx={{ 
            mb: 8,
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
            textAlign: 'center'
          }}>
            <CardContent sx={{ p: 6 }}>
              <Shield sx={{ fontSize: 60, color: 'var(--light-blue-cta)', mb: 3 }} />
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 600, 
                color: 'var(--navy-blue)', 
                mb: 2 
              }}>
                {t('privacy_policy_page.data_security_title', 'Veri Güvenliğiniz Önceliğimiz')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                {t('privacy_policy_page.data_security_desc', 'Gizlilik politikamız hakkında sorularınız varsa bizimle iletişime geçebilirsiniz.')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={`Veri Sorumlusu: ${policyData?.contactInfo.dataController || 'info@hattravel.com'}`}
                  sx={{
                    backgroundColor: 'var(--light-gray-white)',
                    color: 'var(--light-blue-cta)',
                    fontWeight: 500,
                    border: '1px solid var(--gray-shadow)'
                  }}
                />
                <Chip
                  label={`KVKK Başvuru: ${policyData?.contactInfo.kvkkEmail || 'kvkk@hattravel.com'}`}
                  sx={{
                    backgroundColor: 'var(--light-gray-white)',
                    color: 'var(--light-blue-cta)',
                    fontWeight: 500,
                    border: '1px solid var(--gray-shadow)'
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicyPage;